import { useLocation } from "react-router-dom";
import { PageHeader } from "../../components/PageHeader";
import { Chip } from "../../components/Chip";
import { Button } from "../../components/Button";
import { IconButton } from "../../components/IconButton";
import { CheckIcon, PencilIcon, XIcon } from "../../components/icons";
import { Toast } from "../../components/Toast";
import { formatARS } from "../../lib/money";
import { useConsignments, useDiscardConsignment } from "./hooks";
import { Sheet } from "../../components/Sheet";
import type { Consignment } from "./api";
import { useEffect, useState } from "react";

const CONSIGNMENT_FEEDBACK_KEY = "ventas.consignmentFeedback";

export function ConsignmentsView() {
  const location = useLocation();
  const query = useConsignments("open");
  const discard = useDiscardConsignment();
  const [toDiscard, setToDiscard] = useState<Consignment | null>(null);
  const [feedback, setFeedback] = useState<{ kind: "success" | "error"; message: string } | null>(null);

  useEffect(() => {
    const routeFeedback = (location.state as { feedback?: string } | null)?.feedback;
    const pendingFeedback = sessionStorage.getItem(CONSIGNMENT_FEEDBACK_KEY);
    const message = routeFeedback ?? pendingFeedback;
    if (!message) return;
    setFeedback({ kind: "success", message });
    if (pendingFeedback) sessionStorage.removeItem(CONSIGNMENT_FEEDBACK_KEY);
    if (routeFeedback) window.history.replaceState(null, "", window.location.href);
  }, [location.state]);

  const discardOne = async () => {
    if (!toDiscard) return;
    try {
      await discard.mutateAsync(toDiscard.id);
      setToDiscard(null);
      setFeedback({ kind: "success", message: "Consignación descartada y devolución registrada." });
    } catch {
      setFeedback({ kind: "error", message: "No se pudo descartar la consignación. Intentá nuevamente." });
    }
  };

  return (
    <main className="page page--narrow">
      <PageHeader title="Consignaciones" backHref="/more" />
      <section className="page-section consignment-list-section">
        <div className="section-intro">
          <div>
            <p className="eyebrow">Abiertas</p>
            <p>Operaciones pendientes de devolución o conversión en venta.</p>
          </div>
        </div>
        {query.isLoading && <div className="skeleton-list" aria-label="Cargando consignaciones"><span /><span /><span /></div>}
        {query.isError && <div className="empty-state"><strong>No pudimos cargar las consignaciones</strong><span>Revisá la conexión e intentá nuevamente.</span></div>}
        {!query.isLoading && !query.isError && (query.data ?? []).length === 0 && (
          <div className="empty-state"><strong>No hay consignaciones abiertas.</strong><span>Las nuevas se crean desde la vista de venta activando el modo consignación.</span></div>
        )}
        <div className="consignment-list">
          {(query.data ?? []).map((consignment) => (
            <article key={consignment.id} className="consignment-row">
              <div className="consignment-row__identity">
                <div className="consignment-row__title">
                  <strong>Consignación #{consignment.id}</strong>
                  <Chip variant="tag-warning" size="sm">Abierta</Chip>
                </div>
                <span>{consignment.customerNameSnapshot ?? "Sin cliente"}</span>
                <small>{formatConsignmentDate(consignment.createdAt)}</small>
              </div>
              <div className="consignment-row__metrics">
                <Metric label="Total" value={consignment.totalChargedCents} />
                <Metric label="Pagado" value={consignment.totalPaidCents} />
                <Metric label="Devuelto" value={consignment.totalRefundedCents} />
                <Metric label="Saldo" value={consignment.balanceCents} emphasis />
              </div>
              <div className="consignment-row__actions">
                <IconButton
                  to={`/consignaciones/${consignment.id}/editar`}
                  icon={<PencilIcon />}
                  label={`Editar consignación #${consignment.id}`}
                  size="lg"
                  variant="soft"
                />
                <IconButton
                  icon={<XIcon />}
                  label={`Descartar consignación #${consignment.id}`}
                  size="lg"
                  variant="danger"
                  onClick={() => setToDiscard(consignment)}
                  disabled={discard.isPending}
                />
              </div>
            </article>
          ))}
        </div>
      </section>
      {toDiscard && (
        <DiscardSheet
          consignment={toDiscard}
          pending={discard.isPending}
          onClose={() => setToDiscard(null)}
          onConfirm={() => void discardOne()}
        />
      )}
      {feedback && <Toast kind={feedback.kind} message={feedback.message} icon={feedback.kind === "success" ? <CheckIcon /> : undefined} autoCloseMs={2400} onDismiss={() => setFeedback(null)} />}
    </main>
  );
}

function DiscardSheet({
  consignment,
  pending,
  onClose,
  onConfirm,
}: {
  consignment: Consignment;
  pending: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const alreadyRefundedByPayment = new Map<number, number>();
  for (const refund of consignment.refunds) {
    if (refund.paymentId != null) alreadyRefundedByPayment.set(refund.paymentId, (alreadyRefundedByPayment.get(refund.paymentId) ?? 0) + refund.amountCents);
  }
  const byMethod = new Map<string, { name: string; amountCents: number }>();
  for (const payment of consignment.payments) {
    const amount = Math.max(0, payment.amountCents - (alreadyRefundedByPayment.get(payment.id) ?? 0));
    if (amount <= 0) continue;
    const name = payment.paymentMethodSnapshot?.name ?? `Medio #${payment.paymentMethodId}`;
    const existing = byMethod.get(name) ?? { name, amountCents: 0 };
    existing.amountCents += amount;
    byMethod.set(name, existing);
  }
  const refundCents = [...byMethod.values()].reduce((sum, row) => sum + row.amountCents, 0);

  return (
    <Sheet open onClose={onClose} title="Descartar consignación">
      <div className="consignment-discard-sheet">
        <p className="consignment-discard-sheet__help">La operación quedará registrada como descartada y no generará una venta.</p>
        <dl className="summary-ledger__section consignment-discard-sheet__summary">
          <div><dt>Total de la consignación</dt><dd>{formatARS(consignment.totalChargedCents)}</dd></div>
          <div><dt>Total pagado</dt><dd>{formatARS(consignment.totalPaidCents)}</dd></div>
          <div><dt>Total ya devuelto</dt><dd>{formatARS(consignment.totalRefundedCents)}</dd></div>
          <div><dt><strong>Total a devolver</strong></dt><dd><strong>{formatARS(refundCents)}</strong></dd></div>
        </dl>
        {refundCents > 0 && (
          <div className="summary-ledger__section consignment-discard-sheet__methods">
            <strong>Devolución por medio original</strong>
            <dl>
              {[...byMethod.values()].map((row) => <div key={row.name}><dt>{row.name}</dt><dd>−{formatARS(row.amountCents)}</dd></div>)}
            </dl>
          </div>
        )}
        <Button fullWidth size="lg" variant="danger" disabled={pending} onClick={onConfirm}>{pending ? "Descartando…" : "Confirmar descarte"}</Button>
        <Button fullWidth variant="ghost" onClick={onClose} disabled={pending}>Cancelar</Button>
      </div>
    </Sheet>
  );
}

function Metric({ label, value, emphasis }: { label: string; value: number; emphasis?: boolean }) {
  return <div className={emphasis ? "consignment-metric is-emphasis" : "consignment-metric"}><span>{label}</span><strong className="num">{formatARS(value)}</strong></div>;
}

function formatConsignmentDate(value: string) {
  return new Intl.DateTimeFormat("es-AR", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}
